import React, { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';

import { LogoYellow, User } from '../../assets';
import { Feedback, Settings } from '../../assets/index';
import { Role } from '../../models';
import { AppAdminLink, FrontOfficeLink, GeneralLink, RestoAdminLink } from '../../models/Routes';
import { userSelectors } from '../../redux';
import AppSettings from '../../scenes/admin/components/AppSettings';
import FeedbackModal from '../../scenes/frontoffice/components/FeedbackModal';
import { Spinner } from '../index';
import SideBarItem from './SideBarItem';

import './sidebar.scss';

interface Props {
  role: Role;
}

enum sidebarModal {
  SETTINGS = 'SETTINGS',
  FEEDBACK = 'FEEDBACK',
}

const SideBar: FC<Props & RouteComponentProps> = ({ role, history }) => {
  const { t } = useTranslation();
  const isLogoutLoading = useSelector(userSelectors.isLogoutLoading);
  const userInfo = useSelector(userSelectors.getUserInfo);
  const [openedModal, setOpenedModal] = useState<sidebarModal>();

  useEffect(() => {
    if (!isLogoutLoading && !userInfo) history.push(FrontOfficeLink.ROOT);
  }, [isLogoutLoading, userInfo]);

  const getItems = () => {
    switch (role) {
      case Role.APP_ADMIN:
        return (
          <>
            <SideBarItem link={AppAdminLink.FOOD_TYPES} icon={'Cutlery'} text={t('titleFoodTypes')} />
            <SideBarItem link={AppAdminLink.STORES} icon={'Store'} text={t('titleStores')} />
            <SideBarItem link={AppAdminLink.USERS} icon={'Users'} text={t('titleUsers')} />
            <SideBarItem link={AppAdminLink.REPORTS} icon={'Clipboard'} text={t('titleReports')} />
          </>
        );

      case Role.RESTO_ADMIN:
        return (
          <>
            <SideBarItem link={RestoAdminLink.FOOD_TRAY_TEMPLATES} icon={'Raster'} text={t('titleFoodLineTemplates')} />
            <SideBarItem link={RestoAdminLink.USERS} icon={'Users'} text={t('titleUsers')} />
          </>
        );
      default:
        break;
    }
  };

  return (
    <aside className='side-nav'>
      <div className='inner-container'>
        <div>
          <div className='logo-sidebar'>
            <LogoYellow />
            <p>{t('title')}</p>
          </div>
          {getItems()}
        </div>
        <div className='sidebar-links-footer'>
          <button onClick={() => setOpenedModal(sidebarModal.FEEDBACK)} className='sidebar-link'>
            <Feedback className='sidebar-icon' />
            {t('lblFeedback')}
          </button>
          <button onClick={() => setOpenedModal(sidebarModal.SETTINGS)} className='sidebar-link'>
            <Settings className='sidebar-icon' />
            {t('titleSettings')}
          </button>
          {!!userInfo && (
            <div className='sidebar-link logout-link'>
              <User className='sidebar-icon' />
              <div>
                {userInfo.firstName && userInfo.lastName ? (
                  <span>{`${userInfo.firstName} ${userInfo.lastName}`.trim()}</span>
                ) : (
                  <span>{userInfo.email}</span>
                )}
                <span>{userInfo.country.name}</span>
                <a href={GeneralLink.LOGOUT} className=''>
                  {t('lblLogout')}
                </a>
              </div>
              {isLogoutLoading && <Spinner />}
            </div>
          )}
        </div>
      </div>

      <AppSettings closeModal={() => setOpenedModal(null)} isOpen={openedModal === sidebarModal.SETTINGS} fromSideBar />
      <FeedbackModal closeModal={() => setOpenedModal(null)} isOpen={openedModal === sidebarModal.FEEDBACK} />
    </aside>
  );
};

export default withRouter(SideBar);
